import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * SidebarItem Component - Molecular Level
 * 
 * Navigation item for sidebar with nested children support
 * 
 * Features:
 * - Active state indication
 * - Nested navigation with expand/collapse
 * - Icon support
 * - Badge support
 * - Smooth animations
 * - Accessible
 */

export interface SidebarItemProps {
  /** Item label */
  label: string;
  /** Navigation path */
  path?: string;
  /** Icon element or icon name */
  icon?: React.ReactNode;
  /** Badge element (e.g., notification count) */
  badge?: React.ReactNode;
  /** Children items for nested navigation */
  children?: SidebarItemProps[];
  /** Whether sidebar is collapsed */
  isCollapsed?: boolean;
  /** Nesting level (for indentation) */
  level?: number;
  /** Custom onClick handler */
  onClick?: () => void;
  /** Whether item is currently active */
  isActive?: boolean;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  label,
  path,
  icon,
  badge,
  children,
  isCollapsed = false,
  level = 0,
  onClick,
  isActive: isActiveProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const hasChildren = children && children.length > 0;
  const isActive = isActiveProp ?? (path ? location.pathname === path : false);
  const isChildActive = hasChildren && children.some(child => 
    child.path && location.pathname.startsWith(child.path)
  );

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (onClick) {
      onClick();
    } else if (hasChildren) {
      setIsExpanded(!isExpanded);
    } else if (path) {
      navigate(path);
    }
  };

  const paddingLeft = level === 0 ? 'pl-4' : level === 1 ? 'pl-8' : 'pl-12';

  return (
    <div className="w-full">
      <button
        onClick={handleClick}
        className={`
          w-full flex items-center gap-3 py-3 px-4 ${paddingLeft} transition-all duration-200 relative
          ${isActive 
            ? 'bg-gradient-to-r from-[#EAB308]/20 to-transparent text-white font-semibold' 
            : 'text-gray-300 hover:bg-white/10 hover:text-white'
          }
          ${isCollapsed ? 'justify-center' : 'justify-start'}
          ${isChildActive && !isActive ? 'text-white' : ''}
        `}
        aria-expanded={hasChildren ? isExpanded : undefined}
        title={isCollapsed ? label : undefined}
      >
        {/* Active indicator */}
        {isActive && (
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#FFD700] rounded-r" />
        )}

        {/* Icon */}
        {icon && (
          <span className={`flex-shrink-0 ${isCollapsed ? 'text-xl' : 'text-lg'}`}>
            {icon}
          </span>
        )}

        {/* Label */}
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left text-sm whitespace-nowrap overflow-hidden text-ellipsis">
              {label}
            </span>

            {/* Badge */}
            {badge && <span className="flex-shrink-0">{badge}</span>}

            {/* Expand/Collapse indicator */}
            {hasChildren && (
              <svg
                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </>
        )}
      </button>

      {/* Nested children */}
      {hasChildren && !isCollapsed && (
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          {children.map((child, index) => (
            <SidebarItem
              key={child.path || child.label || index}
              {...child}
              level={(level || 0) + 1}
              isCollapsed={isCollapsed}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SidebarItem;

